import {
  Cancel,
  CheckCircle,
  Delete,
  Person,
  Schedule,
} from '@mui/icons-material';
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { useIsMutating } from '@tanstack/react-query';
import { ApprovalStatus } from 'lib/api/approvals/types';
import { validatePermissions } from 'lib/functions/utils';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'services/redux/store';

import { useComiteeTabContext } from './ComiteeTabProvider';
import DeleteApprovalDialog from './DeleteApprovalDialog';
import { ApprovalHistoryCardProps } from './types';

const ApprovalHistoryCard = ({ approval, index }: ApprovalHistoryCardProps) => {
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const user = useSelector((state: RootState) => state.auth.user);

  const { canEdit } = useComiteeTabContext();
  const canDeleteApproval =
    canEdit && validatePermissions(user, 'comitee_delete_approval');

  const status = approval.status === 'APPROVED' ? 'Aprobada' : 'Rechazada';

  const getStatusColor = (status: ApprovalStatus) => {
    switch (status) {
      case 'APPROVED':
        return 'success';
      case 'REJECTED':
        return 'error';
      default:
        return 'warning';
    }
  };

  const handleDeleteClick = () => {
    setOpenDeleteDialog(true);
  };

  const handleDeleteCancel = () => {
    setOpenDeleteDialog(false);
  };

  const isDeleting =
    useIsMutating({
      mutationKey: ['approvals', 'delete'],
      predicate: (m) => m.state.variables === approval.id,
    }) > 0;

  const DeleteLoadingButton = () => {
    return (
      <IconButton
        disabled
        sx={{
          width: 40,
          height: 40,
          cursor: 'default',
        }}
      >
        <CircularProgress
          size={18}
          thickness={5}
          sx={{
            color: 'primary',
          }}
        />
      </IconButton>
    );
  };

  return (
    <>
      <Paper
        key={index}
        sx={{
          p: 2,
          mb: 2,
          boxShadow: 'none',
          border: '1px solid #e0e0e0',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'nowrap',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            minWidth: '30%',
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              backgroundColor:
                approval.status === 'APPROVED'
                  ? 'rgba(46, 125, 50, 0.15)'
                  : 'rgba(237, 108, 2, 0.15)',
              borderRadius: '50%',
              p: 0.75,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {approval.status === 'APPROVED' ? (
              <CheckCircle sx={{ fontSize: '1.5rem', color: 'success.main' }} />
            ) : approval.status === 'REJECTED' ? (
              <Cancel sx={{ fontSize: '1.5rem', color: 'error.main' }} />
            ) : (
              <Schedule sx={{ fontSize: '1.5rem', color: 'warning.main' }} />
            )}
          </Box>
          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                lineHeight: 1,
                fontSize: { xs: '0.95rem', md: '1.05rem' },
              }}
            >
              {approval.user.name}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontSize: '0.75rem',
                lineHeight: 1,
              }}
            >
              {approval.user.email}
            </Typography>
          </Box>
        </Box>

        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

        <Chip
          label={status}
          color={getStatusColor(approval.status)}
          size="small"
          sx={{
            height: 20,
            fontSize: '0.7rem',
            fontWeight: 600,
            flexShrink: 0,
          }}
        />

        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              backgroundColor: 'rgba(128, 128, 128, 0.05)',
              p: 0.75,
              borderRadius: '50%',
              mr: 1,
            }}
          >
            <Person sx={{ fontSize: '0.95rem', color: 'text.secondary' }} />
          </Box>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, fontSize: '0.8rem', lineHeight: 1 }}
          >
            {approval.role.name}
          </Typography>
        </Box>

        {approval.reason && (
          <>
            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                minWidth: 120,
                flex: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: '0.8rem',
                  lineHeight: 1,
                  fontStyle: 'italic',
                  color: 'text.secondary',
                }}
              >
                &ldquo;{approval.reason}&rdquo;
              </Typography>
            </Box>
          </>
        )}

        {canDeleteApproval && approval.userId === user?.id && (
          <>
            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                flexShrink: 0,
              }}
            >
              {isDeleting ? (
                <DeleteLoadingButton />
              ) : (
                <Tooltip title="Eliminar aprobación" arrow>
                  <IconButton
                    onClick={handleDeleteClick}
                    sx={{
                      backgroundColor: 'rgba(244, 67, 54, 0.1)',
                      color: 'error.main',
                      '&:hover': {
                        backgroundColor: 'rgba(244, 67, 54, 0.2)',
                      },
                      width: 40,
                      height: 40,
                    }}
                  >
                    <Delete sx={{ fontSize: '1.2rem' }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </>
        )}
      </Paper>

      {openDeleteDialog && (
        <DeleteApprovalDialog
          open={openDeleteDialog}
          setOpen={setOpenDeleteDialog}
          approval={approval}
          onCancel={handleDeleteCancel}
        />
      )}
    </>
  );
};

export default ApprovalHistoryCard;
